import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuArrowLeft, LuArrowUpRight, LuCalendar, LuBuilding2, LuCompass, LuUser } from 'react-icons/lu';
import { findProjectForRoute, getPortfolioDetailPath, normalizeSlug } from '../utils/portfolioMatching';
import { portfolio as staticPortfolio } from '../data/portfolio';
import { publicApi } from '../services/publicApi';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';

function mapApiProject(item) {
  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    category: item.category || 'Project',
    description: item.description,
    longDescription: item.longDescription || item.description,
    clientName: item.clientName,
    industry: item.industry || item.category,
    year: item.year ? String(item.year) : '2025',
    direction: item.projectDirection || 'Project Delivery',
    websiteUrl: item.websiteUrl,
    image: item.mainImageUrl || '/img/pos system.png',
    galleryImages: Array.isArray(item.galleryImages) ? item.galleryImages : [],
    detailFields: Array.isArray(item.detailFields) ? item.detailFields : [],
  };
}

function mapStaticProject(item) {
  return {
    ...item,
    slug: item.slug || String(item.id),
    longDescription: item.longDescription || item.description,
    clientName: item.clientName,
    websiteUrl: item.websiteUrl,
    galleryImages: item.galleryImages || (item.image ? [item.image] : []),
    detailFields: item.detailFields || [],
  };
}

const PortfolioDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadProject = async () => {
      setLoading(true);
      try {
        const [projects, overviews] = await Promise.all([
          publicApi.getProjects(),
          publicApi.getPortfolioOverviews(),
        ]);

        if (isMounted) {
          const found = findProjectForRoute(projects, overviews, slug);
          if (found) {
            setProject(mapApiProject(found));
            return;
          }
        }
      } catch {
        // Fall back to static content.
      }

      if (isMounted) {
        const found = staticPortfolio.find(
          (item) =>
            normalizeSlug(item.slug || item.id) === normalizeSlug(slug)
            || String(item.id) === String(slug),
        );
        setProject(found ? mapStaticProject(found) : null);
      }
    };

    loadProject().finally(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 bg-gray-50 dark:bg-black transition-colors duration-300">
        <p className="text-gray-500 dark:text-gray-300">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 bg-gray-50 dark:bg-black transition-colors duration-300">
        <h2 className="text-3xl font-bold text-black dark:text-white mb-4">Project Not Found</h2>
        <Link to="/#portfolio">
          <Button variant="primary">Back to Portfolio</Button>
        </Link>
      </div>
    );
  }

  const gallery = project.galleryImages?.length
    ? project.galleryImages
    : project.image
      ? [project.image]
      : [];

  const renderDetailField = (field, index) => {
    if (!field?.label && !field?.value) return null;

    if (field.type === 'image' && field.value) {
      return (
        <div key={index} className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10">
          <p className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#111]">
            {field.label}
          </p>
          <img src={field.value} alt={field.label} className="w-full h-56 object-cover" />
        </div>
      );
    }

    if (field.type === 'url' && field.value) {
      return (
        <div key={index} className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111] p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            {field.label}
          </p>
          <a
            href={field.value}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline break-all"
          >
            {field.value}
            <LuArrowUpRight size={16} />
          </a>
        </div>
      );
    }

    return (
      <div key={index} className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111] p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          {field.label}
        </p>
        <p className="text-gray-700 dark:text-gray-200">{field.value}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black pt-28 pb-20 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-6">
        <Link
          to="/#portfolio"
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-primary mb-8"
        >
          <LuArrowLeft size={16} />
          Back to Portfolio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Badge className="bg-primary text-black text-xs font-extrabold tracking-wider px-3 py-1 rounded-full mb-4">
            {project.category}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold text-black dark:text-white mb-4">
            {project.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl leading-relaxed">
            {project.longDescription}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="rounded-[2rem] overflow-hidden border border-gray-200 dark:border-white/10 mb-10 shadow-[0_20px_60px_rgba(0,0,0,0.08)]"
        >
          <img
            src={project.image}
            alt={project.title}
            className="w-full max-h-[520px] object-cover"
          />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f0f0f] p-5">
            <div className="flex items-center gap-3 mb-2 text-gray-500 dark:text-gray-400">
              <LuCalendar size={18} />
              <span className="text-xs font-bold uppercase">Year</span>
            </div>
            <p className="text-xl font-bold text-black dark:text-white">{project.year}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f0f0f] p-5">
            <div className="flex items-center gap-3 mb-2 text-gray-500 dark:text-gray-400">
              <LuBuilding2 size={18} />
              <span className="text-xs font-bold uppercase">Industry</span>
            </div>
            <p className="text-xl font-bold text-black dark:text-white">{project.industry}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f0f0f] p-5">
            <div className="flex items-center gap-3 mb-2 text-gray-500 dark:text-gray-400">
              <LuCompass size={18} />
              <span className="text-xs font-bold uppercase">Direction</span>
            </div>
            <p className="text-xl font-bold text-black dark:text-white">{project.direction}</p>
          </div>
        </div>

        {(project.clientName || project.websiteUrl) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {project.clientName ? (
              <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f0f0f] p-5">
                <div className="flex items-center gap-3 mb-2 text-gray-500 dark:text-gray-400">
                  <LuUser size={18} />
                  <span className="text-xs font-bold uppercase">Client</span>
                </div>
                <p className="text-xl font-bold text-black dark:text-white">{project.clientName}</p>
              </div>
            ) : null}
            {project.websiteUrl && project.websiteUrl !== '#' ? (
              <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f0f0f] p-5">
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-gray-400 mb-2">Website</p>
                <a
                  href={project.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-black hover:bg-black hover:text-primary transition-colors"
                >
                  Visit Project
                  <LuArrowUpRight size={16} />
                </a>
              </div>
            ) : null}
          </div>
        )}

        {project.detailFields?.length > 0 ? (
          <div className="mb-10">
            <h2 className="text-2xl font-extrabold text-black dark:text-white mb-4">Project Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {project.detailFields.map(renderDetailField)}
            </div>
          </div>
        ) : null}

        {gallery.length > 1 ? (
          <div>
            <h2 className="text-2xl font-extrabold text-black dark:text-white mb-4">Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {gallery.map((image, index) => (
                <div
                  key={`${image}-${index}`}
                  className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10"
                >
                  <img src={image} alt={`${project.title} ${index + 1}`} className="w-full h-64 object-cover" />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PortfolioDetail;
